import React, { useState, useMemo, useContext, useEffect } from 'react';
import { AppContext } from '../src/App';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from './ui/breadcrumb';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Alert, AlertDescription } from './ui/alert';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import {
  Home,
  Download,
  Search,
  Filter,
  CheckCircle,
  XCircle,
  BarChart3,
  AlertTriangle,
  FileText,
  Target,
  Loader2,
  Info,
  Brain,
  Database,
  Settings,
  Calendar,
  User,
  Code,
  ChevronDown,
  ChevronRight,
  Upload
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from './AuthContext';

// Mock data with reference data availability
const mockAuditBatches = [
  {
    id: 'batch-001',
    name: 'Morning Review Batch',
    chartCount: 45,
    date: '2024-01-15',
    chartType: 'Section 28',
    hasReferenceData: true,
    promptVersion: 'v2.1.0'
  },
  {
    id: 'batch-002',
    name: 'Evening Review Batch',
    chartCount: 32,
    date: '2024-01-14',
    chartType: 'Section 17',
    hasReferenceData: true,
    promptVersion: 'v2.1.0'
  },
  {
    id: 'batch-003',
    name: 'Weekend Review Batch',
    chartCount: 28,
    date: '2024-01-13',
    chartType: 'RNA',
    hasReferenceData: false,
    promptVersion: 'v2.0.8'
  },
  {
    id: 'batch-004',
    name: 'Priority Review Batch',
    chartCount: 67,
    date: '2024-01-12',
    chartType: 'Section 28',
    hasReferenceData: true,
    promptVersion: 'v2.1.0'
  },
  {
    id: 'batch-005',
    name: 'Standard Review Batch',
    chartCount: 41,
    date: '2024-01-11',
    chartType: 'Section 17',
    hasReferenceData: false,
    promptVersion: 'v2.0.8'
  }
];

// Mock prompt details
const mockPromptDetails = {
  'v2.1.0': {
    version: 'v2.1.0',
    name: 'Medical Chart Audit Prompt - Enhanced',
    createdDate: '2024-01-10',
    createdBy: 'Dr. Sarah Mitchell',
    status: 'Active',
    description: 'Enhanced prompt with improved medical terminology recognition and comprehensive audit criteria.',
    model: 'GPT-4',
    temperature: 0.1,
    maxTokens: 2048
  },
  'v2.0.8': {
    version: 'v2.0.8',
    name: 'Medical Chart Audit Prompt - Standard',
    createdDate: '2024-01-05',
    createdBy: 'Dr. Michael Chen',
    status: 'Deprecated',
    description: 'Standard medical chart audit prompt with basic compliance checks.',
    model: 'GPT-3.5-turbo',
    temperature: 0.2,
    maxTokens: 1024
  }
};

// Mock reference data for batches that have it
const mockReferenceData = {
  'batch-001': [
    {
      chartId: 'CHART-001',
      auditorDecision: 'Approved',
      auditorReason: 'All documentation complete and accurate',
      auditorName: 'Dr. Sarah Johnson'
    },
    {
      chartId: 'CHART-002',
      auditorDecision: 'Send For Correction',
      auditorReason: 'Missing patient signature on consent form',
      auditorName: 'Dr. Sarah Johnson'
    },
    {
      chartId: 'CHART-003',
      auditorDecision: 'Send For Correction',
      auditorReason: 'Incomplete medical history documentation',
      auditorName: 'Dr. Michael Chen'
    }
  ],
  'batch-002': [
    {
      chartId: 'CHART-004',
      auditorDecision: 'Approved',
      auditorReason: 'Complete documentation, meets all standards',
      auditorName: 'Dr. Emily Rodriguez'
    },
    {
      chartId: 'CHART-005',
      auditorDecision: 'Approved',
      auditorReason: 'All required fields completed properly',
      auditorName: 'Dr. Emily Rodriguez'
    }
  ],
  'batch-004': [
    {
      chartId: 'CHART-006',
      auditorDecision: 'Send For Correction',
      auditorReason: 'Date discrepancy needs clarification',
      auditorName: 'Dr. Robert Kim'
    },
    {
      chartId: 'CHART-007',
      auditorDecision: 'Approved',
      auditorReason: 'Thorough documentation, well organized',
      auditorName: 'Dr. Robert Kim'
    },
    {
      chartId: 'CHART-008',
      auditorDecision: 'Send For Correction',
      auditorReason: 'Missing critical diagnostic information',
      auditorName: 'Dr. Lisa Wang'
    }
  ]
};

// Mock AI decisions for comparison
const mockAIDecisions = {
  'batch-001': [
    {
      chartId: 'CHART-001',
      aiDecision: 'Approved',
      aiConfidence: 92,
      aiReason: 'Documentation appears complete with all required fields filled',
      promptVersion: 'v2.1.0',
      chartType: 'Section 28',
      processedDate: new Date('2024-01-15')
    },
    {
      chartId: 'CHART-002',
      aiDecision: 'Approved',
      aiConfidence: 78,
      aiReason: 'Most documentation complete, minor formatting issues noted',
      promptVersion: 'v2.1.0',
      chartType: 'Section 28',
      processedDate: new Date('2024-01-15')
    },
    {
      chartId: 'CHART-003',
      aiDecision: 'Send For Correction',
      aiConfidence: 85,
      aiReason: 'Missing some medical history details',
      promptVersion: 'v2.1.0',
      chartType: 'Section 28',
      processedDate: new Date('2024-01-15')
    }
  ],
  'batch-002': [
    {
      chartId: 'CHART-004',
      aiDecision: 'Approved',
      aiConfidence: 95,
      aiReason: 'Excellent documentation quality, all standards met',
      promptVersion: 'v2.1.0',
      chartType: 'Section 17',
      processedDate: new Date('2024-01-14')
    },
    {
      chartId: 'CHART-005',
      aiDecision: 'Approved',
      aiConfidence: 89,
      aiReason: 'Good documentation with proper completion',
      promptVersion: 'v2.1.0',
      chartType: 'Section 17',
      processedDate: new Date('2024-01-14')
    }
  ],
  'batch-004': [
    {
      chartId: 'CHART-006',
      aiDecision: 'Approved',
      aiConfidence: 82,
      aiReason: 'Documentation meets basic requirements',
      promptVersion: 'v2.1.0',
      chartType: 'Section 28',
      processedDate: new Date('2024-01-12')
    },
    {
      chartId: 'CHART-007',
      aiDecision: 'Approved',
      aiConfidence: 91,
      aiReason: 'Well-organized and comprehensive documentation',
      promptVersion: 'v2.1.0',
      chartType: 'Section 28',
      processedDate: new Date('2024-01-12')
    },
    {
      chartId: 'CHART-008',
      aiDecision: 'Send For Correction',
      aiConfidence: 76,
      aiReason: 'Some diagnostic information appears incomplete',
      promptVersion: 'v2.1.0',
      chartType: 'Section 28',
      processedDate: new Date('2024-01-12')
    }
  ]
};

interface ComparisonItem {
  chartId: string;
  aiDecision: string;
  aiConfidence: number;
  aiReason: string;
  auditorDecision: string;
  auditorReason: string;
  auditorName: string;
  isMatch: boolean;
  chartType: string;
  processedDate: Date;
  promptVersion: string;
}

interface AccuracyMetrics {
  overall: number;
  matches: number;
  total: number;
  byDecision: {
    [key: string]: {
      matches: number;
      total: number;
    };
  };
}

// types.ts
export interface AuditorDecision {
  id: string;
  chartId: string;
  fileName: string;
  auditorDecision: string;
  auditorReasoning: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  updatedBy: string;
}

export interface AuditorDecisionApiResponse {
  success: boolean;
  message: string;
  data: AuditorDecision[];
  pagination: {
    total: number;
    page: number;
    limit: number;
  };
}


export function AuditDecisionComparison() {
  const { user, token } = useAuth();
  const { setCurrentView, selectedHarvestRunData, setSelectedHarvestRunData } = useContext(AppContext);
  const [selectedBatch, setSelectedBatch] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [comparisonData, setComparisonData] = useState<ComparisonItem[]>([]);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  // Filter and pagination states
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  const [allComparisons, setAllComparisons] = useState<any[]>([]);
  const [data, setData] = useState<any[]>([]);
  const [comparisons, setComparisons] = useState<any[]>([]);
  const [promptDetails, setPromptDetails] = useState<any[]>([]);
  const [shouldShowToast, setShouldShowToast] = useState(true);



  // const fetchAllComparison = async (
  //   id: string,
  //   searchTerm: string = '',
  //   filter: string = 'all'
  // ) => {
  //   if (!id) {
  //     console.warn('No harvestRunId provided');
  //     return;
  //   }

  //   try {
  //     const queryParams = new URLSearchParams();

  //     if (searchTerm) queryParams.append('search', searchTerm);

  //     if (filter === 'matches') {
  //       queryParams.append('filter', 'match');
  //     } else if (filter === 'mismatches') {
  //       queryParams.append('filter', 'unmatch');
  //     }

  //     const response = await fetch(
  //       `${import.meta.env.VITE_API_URL}audits/auditor/decision/${id}?${queryParams.toString()}`,
  //       {
  //         method: 'GET',
  //         headers: {
  //           'Content-Type': 'application/json',
  //           'Authorization': `Bearer ${token}`,
  //         },
  //       }
  //     );

  //     const responseBody = await response.json();

  //     if (!response.ok) {
  //       const errorMessage = responseBody?.message;
  //       throw new Error(errorMessage);
  //     }

  //     toast.success(`Loaded comparison data for ${responseBody?.total} charts.`);
  //     setData(responseBody?.data?.batchOverview);
  //     setComparisons(responseBody?.data?.mergedData);
  //     setPromptDetails(responseBody?.data?.promptDetails)
  //     setAllComparisons(responseBody);
  //     return responseBody;

  //   } catch (error) {
  //     toast.error((error as Error).message);
  //   }
  // };


  const fetchAllComparison = async (
    id: string,
    searchTerm: string = '',
    filter: string = 'all',
    showToast: boolean = true
  ) => {
    if (!id) {
      console.warn('No harvestRunId provided');
      return;
    }

    try {
      const queryParams = new URLSearchParams();

      if (searchTerm) queryParams.append('search', searchTerm);

      if (filter === 'matches') {
        queryParams.append('filter', 'match');
      } else if (filter === 'mismatches') {
        queryParams.append('filter', 'unmatch');
      }

      const response = await fetch(
        `${import.meta.env.VITE_API_URL}audits/auditor/decision/${id}?${queryParams.toString()}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      const responseBody = await response.json();

      if (!response.ok) {
        throw new Error(responseBody?.message);
      }

      if (showToast) {
        toast.success(`Loaded comparison data for ${responseBody?.total} charts.`);
      }

      setData(responseBody?.data?.batchOverview);
      setComparisons(responseBody?.data?.mergedData);
      setPromptDetails(responseBody?.data?.promptDetails);
      setAllComparisons(responseBody);

      return responseBody;

    } catch (error) {
      toast.error((error as Error).message);
    }
  };


  useEffect(() => {
    if (selectedHarvestRunData?.id) {
      fetchAllComparison(selectedHarvestRunData.id, searchTerm, filter, shouldShowToast);
    }
  }, [selectedHarvestRunData?.id]);

  useEffect(() => {
    if (selectedHarvestRunData?.id) {
      fetchAllComparison(selectedHarvestRunData.id, searchTerm, filter, false);
    }
  }, [searchTerm, filter]);



  // useEffect(() => {
  //   if (selectedHarvestRunData?.id) {
  //     fetchAllComparison(selectedHarvestRunData.id, searchTerm, filter);
  //   }
  // }, [selectedHarvestRunData?.id, searchTerm, filter]);




  // Get batches with reference data only, or prioritize selected harvest run data
  // const availableBatches = useMemo(() => {
  //   let batches = mockAuditBatches.filter(batch => batch.hasReferenceData);

  //   // If we have selected harvest run data, add it to the available batches if it's not already there
  //   if (selectedHarvestRunData) {
  //     const harvestBatch = {
  //       id: selectedHarvestRunData.batchId,
  //       name: `Harvest Batch ${selectedHarvestRunData.batchId}`,
  //       chartCount: selectedHarvestRunData.chartsInBatch || selectedHarvestRunData.chartsReviewed || 0,
  //       date: selectedHarvestRunData.runDate?.toLocaleDateString() || 'N/A',
  //       chartType: 'Mixed', // Could be enhanced based on actual data structure
  //       hasReferenceData: true,
  //       promptVersion: selectedHarvestRunData.promptVersion || 'v2.1.0'
  //     };

  //     // Check if this batch is already in the list
  //     const existingBatch = batches.find(b => b.id === harvestBatch.id);
  //     if (!existingBatch) {
  //       batches = [harvestBatch, ...batches];
  //     }
  //   }

  //   return batches;
  // }, [selectedHarvestRunData]);

  // Get prompt details for selected batch
  // const selectedBatchPromptDetails = useMemo(() => {
  //   if (!selectedBatch) return null;
  //   const batch = mockAuditBatches.find(b => b.id === selectedBatch);
  //   if (!batch) return null;
  //   return mockPromptDetails[batch.promptVersion] || null;
  // }, [selectedBatch]);

  // Load comparison data when batch is selected
  // console.log('Selected batch:', selectedBatch, selectedHarvestRunData);
  // const handleBatchSelection = async (batchId: string) => {
  //   setSelectedBatch(batchId);
  //   setIsLoading(true);

  //   try {
  //     // Simulate loading delay
  //     await new Promise(resolve => setTimeout(resolve, 1000));

  //     // Get reference data and AI decisions for the selected batch
  //     let referenceData = mockReferenceData[batchId] || [];
  //     let aiDecisions = mockAIDecisions[batchId] || [];

  //     // If this is a harvest run batch and we don't have mock data, generate some
  //     if ((referenceData.length === 0 || aiDecisions.length === 0) && selectedHarvestRunData && batchId === selectedHarvestRunData.batchId) {
  //       // Generate mock comparison data based on harvest run data
  //       const chartCount = Math.min(10, selectedHarvestRunData.chartsReviewed || 5); // Limit for demo

  //       for (let i = 1; i <= chartCount; i++) {
  //         const chartId = `${batchId}-CHART-${String(i).padStart(3, '0')}`;

  //         // Mock AI decision
  //         const aiDecisionValue = Math.random() > 0.7 ? 'Approved' : 'Send For Correction';
  //         const aiDecision = {
  //           chartId,
  //           aiDecision: aiDecisionValue,
  //           aiConfidence: Math.floor(Math.random() * 30) + 70, // 70-100%
  //           aiReason: aiDecisionValue === 'Approved'
  //             ? 'Documentation appears complete with all required fields filled appropriately'
  //             : 'Some documentation appears incomplete or requires additional clarification',
  //           promptVersion: selectedHarvestRunData.promptVersion || 'v2.1.0',
  //           chartType: 'Mixed',
  //           processedDate: selectedHarvestRunData.runDate || new Date()
  //         };

  //         // Mock auditor decision (sometimes matches, sometimes doesn't)
  //         const shouldMatch = Math.random() > 0.3; // 70% match rate
  //         const auditorDecisionValue = shouldMatch ? aiDecisionValue : (aiDecisionValue === 'Approved' ? 'Send For Correction' : 'Approved');
  //         const auditorDecision = {
  //           chartId,
  //           auditorDecision: auditorDecisionValue,
  //           auditorReason: auditorDecisionValue === 'Approved'
  //             ? 'All documentation complete and meets regulatory standards'
  //             : 'Documentation requires corrections to meet compliance requirements',
  //           auditorName: ['Dr. Sarah Johnson', 'Dr. Michael Chen', 'Dr. Emily Rodriguez'][Math.floor(Math.random() * 3)]
  //         };

  //         aiDecisions.push(aiDecision);
  //         referenceData.push(auditorDecision);
  //       }
  //     }

  //     // Create comparison data
  //     const comparisons: ComparisonItem[] = [];

  //     // Match AI decisions with reference data
  //     aiDecisions.forEach(aiDecision => {
  //       const referenceDecision = referenceData.find(ref => ref.chartId === aiDecision.chartId);

  //       if (referenceDecision) {
  //         comparisons.push({
  //           chartId: aiDecision.chartId,
  //           aiDecision: aiDecision.aiDecision,
  //           aiConfidence: aiDecision.aiConfidence,
  //           aiReason: aiDecision.aiReason,
  //           auditorDecision: referenceDecision.auditorDecision,
  //           auditorReason: referenceDecision.auditorReason,
  //           auditorName: referenceDecision.auditorName,
  //           isMatch: aiDecision.aiDecision === referenceDecision.auditorDecision,
  //           chartType: aiDecision.chartType,
  //           processedDate: aiDecision.processedDate,
  //           promptVersion: aiDecision.promptVersion
  //         });
  //       }
  //     });

  //     setComparisonData(comparisons);
  //     setCurrentPage(1);
  //     // toast.success(`Loaded comparison data for ${comparisons.length} charts`);
  //   } catch (error) {
  //     console.error('Error loading comparison data:', error);
  //     toast.error('Failed to load comparison data');
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };


  // Auto-select batch if coming from harvest run data
  // React.useEffect(() => {
  //   if (selectedHarvestRunData && !selectedBatch) {
  //     const batchId = selectedHarvestRunData.batchId;
  //     console.log('Auto-selecting batch from harvest run data:', batchId);

  //     // Set the selected batch and trigger loading
  //     setSelectedBatch(batchId);
  //     handleBatchSelection(batchId);
  //   }
  // }, [selectedHarvestRunData, selectedBatch]);

  // Calculate accuracy metrics - exclude "Rejected" decisions
  // const accuracyMetrics = useMemo((): AccuracyMetrics | null => {
  //   if (comparisonData.length === 0) return null;

  //   const matches = comparisonData.filter(item => item.isMatch).length;
  //   const total = comparisonData.length;
  //   const overall = (matches / total) * 100;

  //   const byDecision: { [key: string]: { matches: number; total: number } } = {};

  //   comparisonData.forEach(item => {
  //     const decision = item.auditorDecision;
  //     // Only include Approved and Send For Correction decisions
  //     if (decision === 'Approved' || decision === 'Send For Correction') {
  //       if (!byDecision[decision]) {
  //         byDecision[decision] = { matches: 0, total: 0 };
  //       }
  //       byDecision[decision].total++;
  //       if (item.isMatch) {
  //         byDecision[decision].matches++;
  //       }
  //     }
  //   });

  //   return {
  //     overall,
  //     matches,
  //     total,
  //     byDecision
  //   };
  // }, [comparisonData]);

  // Filter data based on search and filters
  // const filteredData = useMemo(() => {
  //   return comparisonData.filter(item => {
  //     const matchesSearch =
  //       item.chartId.toLowerCase().includes(searchTerm.toLowerCase()) ||
  //       item.aiDecision.toLowerCase().includes(searchTerm.toLowerCase()) ||
  //       item.auditorDecision.toLowerCase().includes(searchTerm.toLowerCase()) ||
  //       item.aiReason.toLowerCase().includes(searchTerm.toLowerCase()) ||
  //       item.auditorReason.toLowerCase().includes(searchTerm.toLowerCase());

  //     const matchesFilter =
  //       matchFilter === 'all' ||
  //       (matchFilter === 'matches' && item.isMatch) ||
  //       (matchFilter === 'mismatches' && !item.isMatch);

  //     return matchesSearch && matchesFilter;
  //   });
  // }, [comparisonData, searchTerm, matchFilter]);

  // Paginate filtered data
  const totalPages = Math.ceil(allComparisons.total / itemsPerPage);
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return comparisons.slice(start, start + itemsPerPage);
  }, [comparisons, currentPage, itemsPerPage]);

  // Helper function to truncate text
  // const truncateText = (text: string, maxLength: number = 50) => {
  //   if (text.length <= maxLength) return text;
  //   return text.substring(0, maxLength) + '...';
  // };

  // Handle row expand/collapse
  const toggleRowExpansion = (chartId: string) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(chartId)) {
        newSet.delete(chartId);
      } else {
        newSet.add(chartId);
      }
      return newSet;
    });
  };


  const handleBackToHarvest = () => {
    console.log('Navigating back to chat-harvest');
    setSelectedHarvestRunData(undefined);
    setCurrentView('chat-harvest');
  };


  // Export to CSV
  // const exportToCSV = () => {
  //   if (comparisonData.length === 0) {
  //     toast.error('No data to export');
  //     return;
  //   }

  //   const headers = [
  //     'Chart ID',
  //     'AI Decision',
  //     'AI Reasoning',
  //     'Auditor Decision',
  //     'Auditor Reasoning',
  //     'Auditor Name',
  //     'Match',
  //     'Chart Type',
  //     'Processed Date',
  //     'Prompt Version'
  //   ];

  //   const csvContent = [
  //     headers.join(','),
  //     ...comparisonData.map(item => [
  //       item.chartId,
  //       `"${item.aiDecision}"`,
  //       `"${item.aiReason}"`,
  //       `"${item.auditorDecision}"`,
  //       `"${item.auditorReason}"`,
  //       `"${item.auditorName}"`,
  //       item.isMatch ? 'Yes' : 'No',
  //       `"${item.chartType}"`,
  //       item.processedDate.toLocaleDateString(),
  //       item.promptVersion
  //     ].join(','))
  //   ].join('\n');

  //   const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  //   const link = document.createElement('a');
  //   const url = URL.createObjectURL(blob);
  //   link.setAttribute('href', url);
  //   link.setAttribute('download', `audit-comparison-${selectedBatch}-${new Date().toISOString().split('T')[0]}.csv`);
  //   link.style.visibility = 'hidden';
  //   document.body.appendChild(link);
  //   link.click();
  //   document.body.removeChild(link);

  //   toast.success('Comparison data exported successfully');
  // };

  const exportToCSV = () => {
    const fileData = JSON.stringify(allComparisons, null, 2);
    const blob = new Blob([fileData], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = `audit-comparison-data-${selectedHarvestRunData?.id}.json`;
    link.click();
  };


  return (
    <TooltipProvider>
      <div className="p-6 space-y-6">
        {/* Breadcrumb */}
        {/* <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="#" onClick={() => setCurrentView('dashboard')}>
                <Home className="w-4 h-4" />
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            {selectedHarvestRunData && (
              <>
                <BreadcrumbItem>
                  <BreadcrumbLink href="#" onClick={() => setCurrentView('chat-harvest')}>
                    Chart Harvest
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
              </>
            )}
            <BreadcrumbItem>
              <BreadcrumbPage>Decision Comparison</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb> */}
        <div className="mb-6">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="#" onClick={() => setCurrentView('dashboard')} className="flex items-center gap-2">
                  <Home className="w-3 h-3" />
                  Dashboard
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink href="#" onClick={handleBackToHarvest}>
                  Chart Review
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Decision Comparison</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>

        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="font-inter font-semibold text-2xl leading-[100%] tracking-[0]  mb-2">Audit Decision Comparison</h1>
            <p className="font-inter font-normal text-[16px] leading-[20px] tracking-normal">
              {allComparisons?.data
                ? `Comparing AI decisions against auditor decisions for batch - ${selectedHarvestRunData.display_harvest_id}`
                : 'Auditor review is not yet done for this batch run ID.'
              }
            </p>
          </div>
        </div>

        {/* Batch Information & Performance Metrics */}
        {allComparisons?.data?.mergedData && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[16px]">
                <Database className="w-5 h-5 text-blue-600" />
                Batch Overview & Performance
              </CardTitle>
              <CardDescription className='text-[14px]'>
                Selected batch information and comparison accuracy metrics
              </CardDescription>

              {/* Batch Configuration Details - Horizontal Layout */}
              <div className="flex flex-wrap items-center gap-6 mt-3 pt-3 border-t border-gray-100">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <FileText className="w-4 h-4" />
                  <span className="font-medium">
                    Chart Section - {data?.chartSection}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Brain className="w-4 h-4" />
                  <span>
                    {data?.promptName} v{data?.promptVersionName}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Code className="w-4 h-4" />
                  <span className="font-mono">
                    {data?.aiModel}
                  </span>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Batch Information & Metrics Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-600">Date & Time</label>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    {/* <span className="text-sm text-gray-800">
                      {allComparisons?.data?.dateAndTime
                        ? `${allComparisons?.data?.dateAndTime?.toLocaleDateString()} ${allComparisons?.data?.dateAndTime?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
                        : 'N/A'
                      }
                    </span> */}
                    <span className="text-sm text-gray-800">
                      {data?.dateAndTime
                        ? `${new Date(data.dateAndTime).toLocaleDateString()} ${new Date(data.dateAndTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
                        : 'N/A'
                      }
                    </span>
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-600">Charts in Batch</label>
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-800">
                      {data?.totalCharts}
                    </span>
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-600">Charts Reviewed</label>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-800">
                      {data?.chartsReviewed}
                    </span>
                  </div>
                </div>

                {/* Approved Accuracy */}
                {/* {accuracyMetrics && comparisonData.length > 0 && (
                  <>
                    {accuracyMetrics.byDecision['Approved'] && (
                      <div className="space-y-1">
                        <label className="text-sm font-medium text-gray-600">Approved Accuracy</label>
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                          <span className="text-sm text-gray-800">
                            <strong>
                              {((accuracyMetrics.byDecision['Approved'].matches / accuracyMetrics.byDecision['Approved'].total) * 100).toFixed(1)}%
                            </strong> ({accuracyMetrics.byDecision['Approved'].matches}/{accuracyMetrics.byDecision['Approved'].total})
                          </span>
                        </div>
                      </div>
                    )}

                    Send For Correction Accuracy
                    {accuracyMetrics.byDecision['Send For Correction'] && (
                      <div className="space-y-1">
                        <label className="text-sm font-medium text-gray-600">Correction Accuracy</label>
                        <div className="flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4 text-yellow-600" />
                          <span className="text-sm text-gray-800">
                            <strong>
                              {((accuracyMetrics.byDecision['Send For Correction'].matches / accuracyMetrics.byDecision['Send For Correction'].total) * 100).toFixed(1)}%
                            </strong> ({accuracyMetrics.byDecision['Send For Correction'].matches}/{accuracyMetrics.byDecision['Send For Correction'].total})
                          </span>
                        </div>
                      </div>
                    )}

                    Overall Accuracy - Now inline
                    <div className="space-y-1">
                      <label className="text-sm font-medium text-gray-600">Overall Accuracy</label>
                      <div className="flex items-center gap-2">
                        <Info className="w-4 h-4 text-blue-600" />
                        <span className="text-sm text-gray-800">
                          <strong>{accuracyMetrics.overall.toFixed(1)}%</strong> ({accuracyMetrics.matches}/{accuracyMetrics.total})
                        </span>
                      </div>
                    </div>
                  </>
                )} */}
                {data?.accuracy && (
                  <>
                    {/* Approved Accuracy */}
                    <div className="space-y-1">
                      <label className="text-sm font-medium text-gray-600">Approved Accuracy</label>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span className="text-sm text-gray-800">
                          <strong>{data.accuracy.approvedAccuracy.toFixed(1)}%</strong>
                          ({data?.accuracy?.approvedCorrect}/{data?.accuracy?.approvedTotal})
                        </span>
                      </div>
                    </div>

                    {/* Correction Accuracy */}
                    <div className="space-y-1">
                      <label className="text-sm font-medium text-gray-600">Correction Accuracy</label>
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-yellow-600" />
                        <span className="text-sm text-gray-800">
                          <strong>{data.accuracy.correctionAccuracy.toFixed(1)}%</strong>
                          ({data?.accuracy?.correctionCorrect}/{data?.accuracy?.correctionTotal})
                        </span>
                      </div>
                    </div>

                    {/* Overall Accuracy */}
                    <div className="space-y-1">
                      <label className="text-sm font-medium text-gray-600">Overall Accuracy</label>
                      <div className="flex items-center gap-2">
                        <Info className="w-4 h-4 text-blue-600" />
                        <span className="text-sm text-gray-800">
                          <strong>{data.accuracy.overallAccuracy.toFixed(1)}%</strong>
                          ({data?.accuracy?.overallCorrect}/{data?.accuracy?.overallTotal})
                        </span>
                      </div>
                    </div>
                  </>
                )}
              </div>
              {isLoading && (
                <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
                    <span className="font-medium text-blue-900">Loading comparison data...</span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}


        {/* Prompt Details */}
        {allComparisons?.data && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[16px]">
                <Code className="w-5 h-5 text-blue-600" />
                Prompt Details
              </CardTitle>
              <CardDescription className='text-[14px]'>
                Information about the AI prompt used for this batch
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-600">Version</label>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">Version - {promptDetails?.version}</Badge>
                    {/* <Badge variant={promptDetails?.status === 'Active' ? 'default' : 'secondary'}> */}
                    <Badge variant='outline'>
                      Status - {promptDetails?.status}
                    </Badge>
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-600">Created By</label>
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-800">{promptDetails?.createdBy}</span>
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-600">Created Date</label>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-800">
                      {promptDetails?.createdAt ? new Date(promptDetails.createdAt).toLocaleDateString() : 'N/A'}
                    </span>
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-600">Model</label>
                  <div className="flex items-center gap-2">
                    <Settings className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-800">{promptDetails?.model}</span>
                  </div>
                </div>
              </div>

              <div className="mt-4 space-y-1">
                <label className="text-sm font-medium text-gray-600">Description</label>
                <p className="text-sm text-gray-800 p-3 bg-gray-50 rounded-md">
                  {promptDetails?.description}
                </p>
              </div>

              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-600">Temperature: </label>
                  <span className="text-sm text-gray-800">{promptDetails?.temperature}</span>
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-600">Max Tokens: </label>
                  <span className="text-sm text-gray-800">{promptDetails?.maxTokens}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}



        {/* Comparison Results */}
        {allComparisons?.data && (
          <Card>
            <CardHeader>
              <div>
                <CardTitle className='flex items-center gap-2 text-[16px]'>Comparison Results</CardTitle>
                <CardDescription className='text-[14px]'>
                  Showing {comparisons?.length} of {allComparisons?.total} comparisons • Click the expand icon to view detailed comparison
                </CardDescription>

                {/* Filters and Actions below title */}
                <div className="flex flex-col sm:flex-row gap-4 mt-4">
                  <div className="flex-1">
                    <div className="relative">
                      <Filter className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <Search className="w-4 h-4 absolute left-9 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <Input
                        placeholder="Search by Chart ID, Auditor decision, or AI decision..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-16"
                      />
                    </div>
                  </div>

                  {/* <Select value={matchFilter} onValueChange={setMatchFilter}>
                    <SelectTrigger className="w-full sm:w-48">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Results</SelectItem>
                      <SelectItem value="matches">Matches Only</SelectItem>
                      <SelectItem value="mismatches">Mismatches Only</SelectItem>
                    </SelectContent>
                  </Select> */}
                  <Select value={filter} onValueChange={setFilter}>
                    <SelectTrigger className="w-full sm:w-48">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Results</SelectItem>
                      <SelectItem value="matches">Matches Only</SelectItem>
                      <SelectItem value="mismatches">Mismatches Only</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    onClick={exportToCSV}
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    Export JSON
                  </Button>
                  {/* <Button
                    onClick={handleDownload}
                    className="flex items-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    Download JSON
                  </Button> */}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12"></TableHead>
                      <TableHead className='font-inter font-semibold text-[14px] leading-[100%] tracking-[-0.5%] uppercase text-[#758195]'>Chart ID</TableHead>
                      <TableHead className='font-inter font-semibold text-[14px] leading-[100%] tracking-[-0.5%] uppercase text-[#758195]'>AI Decision</TableHead>
                      <TableHead className='font-inter font-semibold text-[14px] leading-[100%] tracking-[-0.5%] uppercase text-[#758195]'>AI Reasoning</TableHead>
                      <TableHead className='font-inter font-semibold text-[14px] leading-[100%] tracking-[-0.5%] uppercase text-[#758195]'>Auditor Decision</TableHead>
                      <TableHead className='font-inter font-semibold text-[14px] leading-[100%] tracking-[-0.5%] uppercase text-[#758195]'>Auditor Reasoning</TableHead>
                      <TableHead className='font-inter font-semibold text-[14px] leading-[100%] tracking-[-0.5%] uppercase text-[#758195]'>Match</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {allComparisons?.total === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center text-gray-500">
                          No data found
                        </TableCell>
                      </TableRow>
                    ) : (
                      comparisons.map((item, index) => (
                        <React.Fragment key={index}>
                          <TableRow
                            className="hover:bg-gray-50 cursor-pointer"
                            onClick={() => toggleRowExpansion(item?.chartId)}
                          >
                            <TableCell>
                              <div className="flex items-center justify-center">
                                {expandedRows.has(item?.chartId) ? (
                                  <ChevronDown className="w-4 h-4 text-gray-400" />
                                ) : (
                                  <ChevronRight className="w-4 h-4 text-gray-400" />
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="font-mono text-sm">{item?.chartId}</TableCell>
                            <TableCell>
                              <Badge variant={
                                item?.aiDecision === 'Approved' ? 'default' :
                                  item?.aiDecision === 'Send For Correction' ? 'secondary' : 'destructive'
                              }>
                                {item?.aiDecision}
                              </Badge>
                            </TableCell>
                            <TableCell className="max-w-xs">
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="truncate cursor-help">
                                    {(item?.aiReasoning)}
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="max-w-md">
                                  <p>{item?.aiReasoning}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TableCell>
                            <TableCell>
                              <Badge variant={
                                item.auditorDecision === 'Approved' ? 'default' :
                                  item.auditorDecision === 'Send For Correction' ? 'secondary' :
                                    item.auditorDecision === 'Not Found' ? 'outline' : 'destructive'
                              }>
                                {item.auditorDecision}
                              </Badge>
                            </TableCell>
                            <TableCell className="max-w-xs">
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="truncate cursor-help">
                                    {(item.auditorReasoning)}
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="max-w-md">
                                  <p>{item.auditorReasoning}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TableCell>
                            <TableCell>
                              {item.isMatched ? (
                                <CheckCircle className="w-4 h-4 text-green-600" />
                              ) : (
                                <XCircle className="w-4 h-4 text-red-600" />
                              )}
                            </TableCell>
                          </TableRow>
                          {/* Expanded row content */}
                          {expandedRows.has(item.chartId) && (
                            <TableRow>
                              <TableCell colSpan={7} className="bg-gray-50 p-3">
                                <div className="grid grid-cols-2 gap-4">
                                  {/* AI Reasoning */}
                                  <div className="text-sm text-gray-700 p-3 bg-blue-50 rounded border-l-2 border-blue-200 break-words whitespace-pre-wrap">
                                    {item?.aiReasoning}
                                  </div>

                                  {/* Auditor Reasoning */}
                                  <div className="text-sm text-gray-700 p-3 bg-green-50 rounded border-l-2 border-green-200 break-words whitespace-pre-wrap">
                                    {item?.auditorReasoning}
                                  </div>
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-gray-600">
                    Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredData.length)} of {filteredData.length} results
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </Button>
                    <span className="text-sm text-gray-600">
                      Page {currentPage} of {totalPages}
                    </span>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}


      </div>
    </TooltipProvider>
  );
}